import type {
  AnnouncementAttachmentInput,
  AnnouncementAttachmentReadModel,
  AnnouncementFeed,
  AnnouncementManagementDetail,
  AnnouncementMutationOperation,
  AnnouncementMutationResult,
  CreateAnnouncementDraftRequest,
  MarkAnnouncementDeliveryRequest,
  PublishAnnouncementRequest,
  ReplyAnnouncementRequest,
  RetryAnnouncementDeliveryRequest,
  UpdateAnnouncementDraftRequest,
  WithdrawAnnouncementRequest,
} from "@billiard/contracts";
import {
  isAnnouncementFeed,
  isAnnouncementManagementDetail,
  isAnnouncementMutationResult,
  isCreateAnnouncementDraftRequest,
  isMarkAnnouncementDeliveryRequest,
  isPublishAnnouncementRequest,
  isReplyAnnouncementRequest,
  isRetryAnnouncementDeliveryRequest,
  isUpdateAnnouncementDraftRequest,
  isWithdrawAnnouncementRequest,
} from "@billiard/contracts";
import { ApiRequestError, requestOperatorBinary, requestOperatorJson } from "./api";

const MAX_ATTACHMENT_COUNT = 3;
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;
const MAX_TOTAL_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const STABLE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;
const SHA256_PATTERN = /^[0-9a-f]{64}$/u;
const ALLOWED_ATTACHMENT_TYPES = new Set<AnnouncementAttachmentInput["contentType"]>([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);
const ATTACHMENT_EXTENSIONS: Record<AnnouncementAttachmentInput["contentType"], readonly string[]> =
  {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  };

export interface AnnouncementApiContext {
  tenantId: string;
  storeId: string;
  accountId: string;
}

export type AnnouncementMutationRequest =
  | CreateAnnouncementDraftRequest
  | UpdateAnnouncementDraftRequest
  | PublishAnnouncementRequest
  | WithdrawAnnouncementRequest
  | RetryAnnouncementDeliveryRequest
  | MarkAnnouncementDeliveryRequest
  | ReplyAnnouncementRequest;

export interface AnnouncementMutationCommand {
  context: AnnouncementApiContext;
  action: AnnouncementMutationOperation;
  announcementId: string | null;
  method: "POST" | "PUT";
  path: string;
  idempotencyKey: string;
  bodySha256: string;
  request: AnnouncementMutationRequest;
}

export interface AnnouncementAttachmentDownload {
  bytes: Uint8Array;
  fileName: string;
  contentType: AnnouncementAttachmentInput["contentType"];
  etag: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function contextIsValid(value: unknown): value is AnnouncementApiContext {
  return (
    isRecord(value) &&
    typeof value.tenantId === "string" &&
    STABLE_ID_PATTERN.test(value.tenantId) &&
    typeof value.storeId === "string" &&
    STABLE_ID_PATTERN.test(value.storeId) &&
    typeof value.accountId === "string" &&
    STABLE_ID_PATTERN.test(value.accountId)
  );
}

function attachmentNameMatchesType(
  fileName: string,
  contentType: AnnouncementAttachmentInput["contentType"],
): boolean {
  const normalizedName = fileName.toLowerCase();
  return (
    fileName === fileName.trim() &&
    fileName !== "." &&
    fileName !== ".." &&
    !fileName.includes("/") &&
    !fileName.includes("\\") &&
    !/[\p{Cc}\p{Cf}]/u.test(fileName) &&
    ATTACHMENT_EXTENSIONS[contentType].some((extension) => normalizedName.endsWith(extension))
  );
}

function stableJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((item) => stableJson(item)).join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`)
    .join(",")}}`;
}

async function sha256Hex(value: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new ApiRequestError(
      "ANNOUNCEMENT_ATTACHMENT_HASH_UNAVAILABLE",
      "当前设备无法安全计算附件摘要，系统没有读取或发送文件",
    );
  }
  const source = new Uint8Array(value.byteLength);
  source.set(value);
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest("SHA-256", source));
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function requestSha256(request: AnnouncementMutationRequest): Promise<string> {
  return sha256Hex(new TextEncoder().encode(stableJson(request)));
}

function routeFor(
  action: AnnouncementMutationOperation,
  announcementId: string | null,
): { method: "POST" | "PUT"; path: string } | null {
  if (action === "CREATE_DRAFT") {
    return announcementId === null ? { method: "POST", path: "/v1/announcements" } : null;
  }
  if (!announcementId || !STABLE_ID_PATTERN.test(announcementId)) return null;
  const root = `/v1/announcements/${encodeURIComponent(announcementId)}`;
  if (action === "UPDATE_DRAFT") return { method: "PUT", path: root };
  if (action === "PUBLISH") return { method: "POST", path: `${root}/publish` };
  if (action === "WITHDRAW") return { method: "POST", path: `${root}/withdraw` };
  if (action === "RETRY_DELIVERY") return { method: "POST", path: `${root}/retry-delivery` };
  if (action === "DELIVER" || action === "READ" || action === "ACKNOWLEDGE") {
    return { method: "POST", path: `${root}/delivery` };
  }
  if (action === "REPLY") return { method: "POST", path: `${root}/replies` };
  return null;
}

function requestMatchesAction(
  action: AnnouncementMutationOperation,
  request: unknown,
): request is AnnouncementMutationRequest {
  if (action === "CREATE_DRAFT") return isCreateAnnouncementDraftRequest(request);
  if (action === "UPDATE_DRAFT") return isUpdateAnnouncementDraftRequest(request);
  if (action === "PUBLISH") return isPublishAnnouncementRequest(request);
  if (action === "WITHDRAW") return isWithdrawAnnouncementRequest(request);
  if (action === "RETRY_DELIVERY") return isRetryAnnouncementDeliveryRequest(request);
  if (action === "REPLY") return isReplyAnnouncementRequest(request);
  return isMarkAnnouncementDeliveryRequest(request) && request.action === action;
}

export async function buildAnnouncementMutationCommand(
  context: AnnouncementApiContext,
  action: AnnouncementMutationOperation,
  announcementId: string | null,
  request: AnnouncementMutationRequest,
): Promise<AnnouncementMutationCommand> {
  const route = routeFor(action, announcementId);
  if (!contextIsValid(context) || !route || !requestMatchesAction(action, request)) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "公告操作的账号、门店、公告或填写内容不完整，系统没有发送请求",
      400,
    );
  }
  const command: AnnouncementMutationCommand = {
    context: { ...context },
    action,
    announcementId,
    ...route,
    idempotencyKey: request.idempotencyKey,
    bodySha256: await requestSha256(request),
    request: structuredClone(request),
  };
  return Object.freeze(command);
}

export function announcementMutationCommandStructureIsValid(
  value: unknown,
): value is AnnouncementMutationCommand {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "context",
      "action",
      "announcementId",
      "method",
      "path",
      "idempotencyKey",
      "bodySha256",
      "request",
    ]) ||
    !contextIsValid(value.context)
  ) {
    return false;
  }
  const action = value.action as AnnouncementMutationOperation;
  if (value.announcementId !== null && typeof value.announcementId !== "string") return false;
  const announcementId = value.announcementId as string | null;
  const route = routeFor(action, announcementId);
  if (
    !route ||
    value.method !== route.method ||
    value.path !== route.path ||
    typeof value.idempotencyKey !== "string" ||
    typeof value.bodySha256 !== "string" ||
    !SHA256_PATTERN.test(value.bodySha256) ||
    !requestMatchesAction(action, value.request) ||
    value.idempotencyKey !== value.request.idempotencyKey
  ) {
    return false;
  }
  return true;
}

export async function isAnnouncementMutationCommand(value: unknown): Promise<boolean> {
  return (
    announcementMutationCommandStructureIsValid(value) &&
    (await requestSha256(value.request)) === value.bodySha256
  );
}

function parseFeed(value: unknown, context: AnnouncementApiContext): AnnouncementFeed {
  if (
    !contextIsValid(context) ||
    !isAnnouncementFeed(value) ||
    value.items.some(
      (item) =>
        item.announcement.tenantId !== context.tenantId ||
        item.announcement.storeId !== context.storeId,
    )
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "公告列表返回内容无法安全确认，旧列表已隐藏，请重新读取",
    );
  }
  return value;
}

function parseManagement(
  value: unknown,
  context: AnnouncementApiContext,
  announcementId?: string,
): AnnouncementManagementDetail {
  if (
    !contextIsValid(context) ||
    !isAnnouncementManagementDetail(value) ||
    value.announcement.tenantId !== context.tenantId ||
    value.announcement.storeId !== context.storeId ||
    (announcementId !== undefined && value.announcement.announcementId !== announcementId)
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "公告管理详情返回内容无法安全确认，系统已停止展示",
    );
  }
  return value;
}

export function announcementMutationResultMatchesCommand(
  value: unknown,
  command: AnnouncementMutationCommand,
): value is AnnouncementMutationResult {
  if (!isAnnouncementMutationResult(value)) return false;
  const isDeliveryAction =
    command.action === "DELIVER" || command.action === "READ" || command.action === "ACKNOWLEDGE";
  const expectedDeliveryState =
    command.action === "DELIVER"
      ? "DELIVERED"
      : command.action === "READ"
        ? "READ"
        : command.action === "ACKNOWLEDGE"
          ? "ACKNOWLEDGED"
          : null;
  const request = command.request;
  const expectedVersion =
    command.action === "CREATE_DRAFT"
      ? 1
      : command.action === "UPDATE_DRAFT" ||
          command.action === "PUBLISH" ||
          command.action === "WITHDRAW"
        ? (request as UpdateAnnouncementDraftRequest | PublishAnnouncementRequest).expectedVersion +
          1
        : command.action === "RETRY_DELIVERY"
          ? (request as RetryAnnouncementDeliveryRequest).expectedVersion
          : (request as MarkAnnouncementDeliveryRequest | ReplyAnnouncementRequest)
              .expectedAnnouncementVersion;
  const lifecycleMatches =
    command.action === "CREATE_DRAFT" || command.action === "UPDATE_DRAFT"
      ? value.announcement.lifecycle === "DRAFT"
      : command.action === "PUBLISH"
        ? value.announcement.lifecycle === "SCHEDULED" || value.announcement.lifecycle === "ACTIVE"
        : command.action === "WITHDRAW"
          ? value.announcement.lifecycle === "WITHDRAWN" &&
            value.announcement.withdrawalReason === (request as WithdrawAnnouncementRequest).reason
          : value.announcement.lifecycle !== "DRAFT" &&
            value.announcement.lifecycle !== "WITHDRAWN";
  const contentMatches =
    command.action !== "CREATE_DRAFT" && command.action !== "UPDATE_DRAFT"
      ? true
      : (() => {
          const content = request as
            | CreateAnnouncementDraftRequest
            | UpdateAnnouncementDraftRequest;
          return (
            value.announcement.title === content.title &&
            value.announcement.body === content.body &&
            value.announcement.pinned === content.pinned &&
            value.announcement.startsAt === content.startsAt &&
            value.announcement.expiresAt === content.expiresAt &&
            value.announcement.audienceRoles.length === content.audienceRoles.length &&
            value.announcement.audienceRoles.every(
              (role, index) => role === content.audienceRoles[index],
            ) &&
            value.announcement.attachments.length === content.attachments.length &&
            value.announcement.attachments.every((attachment, index) => {
              const input = content.attachments[index];
              return (
                input !== undefined &&
                attachment.fileName === input.fileName &&
                attachment.contentType === input.contentType &&
                attachment.sizeBytes === input.sizeBytes &&
                attachment.sha256 === input.sha256
              );
            })
          );
        })();
  return (
    value.operation === command.action &&
    value.announcement.tenantId === command.context.tenantId &&
    value.announcement.storeId === command.context.storeId &&
    (command.announcementId === null ||
      value.announcement.announcementId === command.announcementId) &&
    value.announcement.version === expectedVersion &&
    lifecycleMatches &&
    contentMatches &&
    (command.action === "REPLY"
      ? value.reply !== null &&
        value.reply.body === (command.request as ReplyAnnouncementRequest).body &&
        value.deliveryState !== null
      : value.reply === null) &&
    (command.action === "REPLY"
      ? value.deliveryState !== null
      : isDeliveryAction
        ? value.deliveryState === expectedDeliveryState
        : value.deliveryState === null)
  );
}

function parseMutationResult(
  value: unknown,
  command: AnnouncementMutationCommand,
): AnnouncementMutationResult {
  if (!announcementMutationResultMatchesCommand(value, command)) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "公告操作回执与原请求不一致，结果暂时无法安全确认",
    );
  }
  return value;
}

async function executeMutation(
  command: AnnouncementMutationCommand,
): Promise<AnnouncementMutationResult> {
  if (!(await isAnnouncementMutationCommand(command))) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "本机保存的公告原请求不完整，系统没有发送或重试",
      400,
    );
  }
  const value = await requestOperatorJson<unknown>(command.path, {
    method: command.method,
    cache: "no-store",
    body: JSON.stringify(command.request),
  });
  return parseMutationResult(value, command);
}

function executeExpectedMutation(
  command: AnnouncementMutationCommand,
  actions: readonly AnnouncementMutationOperation[],
): Promise<AnnouncementMutationResult> {
  if (!actions.includes(command.action)) {
    return Promise.reject(
      new ApiRequestError(
        "REQUEST_VALIDATION_FAILED",
        "公告操作与当前按钮不一致，系统没有发送请求",
        400,
      ),
    );
  }
  return executeMutation(command);
}

export async function listAnnouncementFeed(
  context: AnnouncementApiContext,
  signal?: AbortSignal,
): Promise<AnnouncementFeed> {
  const value = await requestOperatorJson<unknown>("/v1/announcements/feed", {
    cache: "no-store",
    ...(signal ? { signal } : {}),
  });
  return parseFeed(value, context);
}

export async function listAnnouncementManagement(
  context: AnnouncementApiContext,
  signal?: AbortSignal,
): Promise<AnnouncementManagementDetail[]> {
  const value = await requestOperatorJson<unknown>("/v1/announcements/manage", {
    cache: "no-store",
    ...(signal ? { signal } : {}),
  });
  if (!Array.isArray(value) || value.length > 200) {
    throw new ApiRequestError("GATEWAY_RESPONSE_INVALID", "公告管理列表返回内容无法安全确认");
  }
  return value.map((item) => parseManagement(item, context));
}

export async function getAnnouncementManagement(
  context: AnnouncementApiContext,
  announcementId: string,
  signal?: AbortSignal,
): Promise<AnnouncementManagementDetail> {
  if (!STABLE_ID_PATTERN.test(announcementId)) {
    throw new ApiRequestError("REQUEST_VALIDATION_FAILED", "公告编号格式不正确", 400);
  }
  const value = await requestOperatorJson<unknown>(
    `/v1/announcements/${encodeURIComponent(announcementId)}/manage`,
    { cache: "no-store", ...(signal ? { signal } : {}) },
  );
  return parseManagement(value, context, announcementId);
}

export async function getAnnouncementMutationByRequest(
  command: AnnouncementMutationCommand,
  signal?: AbortSignal,
): Promise<AnnouncementMutationResult | null> {
  if (!(await isAnnouncementMutationCommand(command))) {
    throw new ApiRequestError(
      "REQUEST_VALIDATION_FAILED",
      "本机保存的公告原请求不完整，系统没有查询或重试",
      400,
    );
  }
  try {
    const value = await requestOperatorJson<unknown>(
      `/v1/announcements/by-request/${encodeURIComponent(command.idempotencyKey)}`,
      { cache: "no-store", ...(signal ? { signal } : {}) },
    );
    return parseMutationResult(value, command);
  } catch (error) {
    if (
      error instanceof ApiRequestError &&
      error.statusCode === 404 &&
      error.code === "ANNOUNCEMENT_REQUEST_RESULT_NOT_FOUND"
    ) {
      return null;
    }
    throw error;
  }
}

export function createAnnouncementDraft(
  command: AnnouncementMutationCommand,
): Promise<AnnouncementMutationResult> {
  return executeExpectedMutation(command, ["CREATE_DRAFT"]);
}

export function updateAnnouncementDraft(
  command: AnnouncementMutationCommand,
): Promise<AnnouncementMutationResult> {
  return executeExpectedMutation(command, ["UPDATE_DRAFT"]);
}

export function publishAnnouncement(
  command: AnnouncementMutationCommand,
): Promise<AnnouncementMutationResult> {
  return executeExpectedMutation(command, ["PUBLISH"]);
}

export function withdrawAnnouncement(
  command: AnnouncementMutationCommand,
): Promise<AnnouncementMutationResult> {
  return executeExpectedMutation(command, ["WITHDRAW"]);
}

export function retryAnnouncementDelivery(
  command: AnnouncementMutationCommand,
): Promise<AnnouncementMutationResult> {
  return executeExpectedMutation(command, ["RETRY_DELIVERY"]);
}

export function markAnnouncementDelivery(
  command: AnnouncementMutationCommand,
): Promise<AnnouncementMutationResult> {
  return executeExpectedMutation(command, ["DELIVER", "READ", "ACKNOWLEDGE"]);
}

export function replyAnnouncement(
  command: AnnouncementMutationCommand,
): Promise<AnnouncementMutationResult> {
  return executeExpectedMutation(command, ["REPLY"]);
}

function readFile(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(
        new ApiRequestError("ANNOUNCEMENT_ATTACHMENT_READ_FAILED", `无法读取附件“${file.name}”`),
      );
    reader.onabort = reader.onerror;
    reader.onload = () => {
      if (!(reader.result instanceof ArrayBuffer)) {
        reject(
          new ApiRequestError("ANNOUNCEMENT_ATTACHMENT_READ_FAILED", `无法读取附件“${file.name}”`),
        );
        return;
      }
      resolve(new Uint8Array(reader.result));
    };
    reader.readAsArrayBuffer(file);
  });
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary);
}

export async function prepareAnnouncementAttachments(
  files: readonly File[],
): Promise<AnnouncementAttachmentInput[]> {
  if (files.length > MAX_ATTACHMENT_COUNT) {
    throw new ApiRequestError(
      "ANNOUNCEMENT_ATTACHMENT_COUNT_INVALID",
      "每份公告最多上传 3 个附件",
      400,
    );
  }
  let totalBytes = 0;
  for (const file of files) {
    totalBytes += file.size;
    if (
      file.size < 1 ||
      file.size > MAX_ATTACHMENT_BYTES ||
      file.name !== file.name.trim() ||
      file.name.length < 1 ||
      file.name.length > 120 ||
      !ALLOWED_ATTACHMENT_TYPES.has(file.type as AnnouncementAttachmentInput["contentType"]) ||
      !attachmentNameMatchesType(file.name, file.type as AnnouncementAttachmentInput["contentType"])
    ) {
      throw new ApiRequestError(
        "ANNOUNCEMENT_ATTACHMENT_INVALID",
        `附件“${file.name || "未命名文件"}”的格式、名称或大小不符合要求；单个文件不能超过 2 MiB`,
        400,
      );
    }
  }
  if (totalBytes > MAX_TOTAL_ATTACHMENT_BYTES) {
    throw new ApiRequestError(
      "ANNOUNCEMENT_ATTACHMENT_TOTAL_TOO_LARGE",
      "全部附件合计不能超过 5 MiB",
      400,
    );
  }
  return Promise.all(
    files.map(async (file) => {
      const bytes = await readFile(file);
      if (bytes.byteLength !== file.size) {
        throw new ApiRequestError(
          "ANNOUNCEMENT_ATTACHMENT_READ_FAILED",
          `附件“${file.name}”读取前后大小不一致，系统没有发送文件`,
        );
      }
      return {
        fileName: file.name,
        contentType: file.type as AnnouncementAttachmentInput["contentType"],
        sizeBytes: bytes.byteLength,
        sha256: await sha256Hex(bytes),
        contentBase64: bytesToBase64(bytes),
      };
    }),
  );
}

function contentDispositionFileName(value: string | null): string | null {
  const match = /^attachment; filename="([^"\\\r\n]+)"; filename\*=UTF-8''([^;]+)$/iu.exec(
    value ?? "",
  );
  if (!match?.[1] || !match[2]) return null;
  if (
    !Array.from(match[1]).every((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint >= 0x20 && codePoint <= 0x7e;
    })
  ) {
    return null;
  }
  try {
    return decodeURIComponent(match[2]);
  } catch {
    return null;
  }
}

function attachmentReadModelIsValid(
  value: unknown,
  announcementId: string,
): value is AnnouncementAttachmentReadModel {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "attachmentId",
      "fileName",
      "contentType",
      "sizeBytes",
      "sha256",
      "downloadPath",
    ])
  ) {
    return false;
  }
  return (
    typeof value.attachmentId === "string" &&
    STABLE_ID_PATTERN.test(value.attachmentId) &&
    typeof value.fileName === "string" &&
    value.fileName.length >= 1 &&
    value.fileName.length <= 120 &&
    ALLOWED_ATTACHMENT_TYPES.has(value.contentType as AnnouncementAttachmentInput["contentType"]) &&
    attachmentNameMatchesType(
      value.fileName,
      value.contentType as AnnouncementAttachmentInput["contentType"],
    ) &&
    Number.isInteger(value.sizeBytes) &&
    Number(value.sizeBytes) >= 1 &&
    Number(value.sizeBytes) <= MAX_ATTACHMENT_BYTES &&
    typeof value.sha256 === "string" &&
    SHA256_PATTERN.test(value.sha256) &&
    value.downloadPath === `/v1/announcements/${announcementId}/attachments/${value.attachmentId}`
  );
}

export async function downloadAnnouncementAttachment(
  context: AnnouncementApiContext,
  announcementId: string,
  attachment: AnnouncementAttachmentReadModel,
  signal?: AbortSignal,
): Promise<AnnouncementAttachmentDownload> {
  if (
    !contextIsValid(context) ||
    !STABLE_ID_PATTERN.test(announcementId) ||
    !attachmentReadModelIsValid(attachment, announcementId)
  ) {
    throw new ApiRequestError("REQUEST_VALIDATION_FAILED", "公告附件信息不完整，系统没有下载", 400);
  }
  const response = await requestOperatorBinary(attachment.downloadPath, {
    cache: "no-store",
    ...(signal ? { signal } : {}),
  });
  const contentType = response.headers.get("content-type");
  const contentLength = response.headers.get("content-length");
  const etag = response.headers.get("etag");
  const fileName = contentDispositionFileName(response.headers.get("content-disposition"));
  const cacheControl = response.headers.get("cache-control");
  const downloadedSha256 = await sha256Hex(response.bytes);
  if (
    contentType !== attachment.contentType ||
    contentLength !== String(attachment.sizeBytes) ||
    response.bytes.byteLength !== attachment.sizeBytes ||
    etag !== `"${attachment.sha256}"` ||
    downloadedSha256 !== attachment.sha256 ||
    fileName !== attachment.fileName ||
    !/(?:^|,)\s*no-store\s*(?:,|$)/iu.test(cacheControl ?? "")
  ) {
    throw new ApiRequestError(
      "GATEWAY_RESPONSE_INVALID",
      "公告附件的类型、大小、摘要或文件名无法安全确认，系统已停止下载",
      response.statusCode,
    );
  }
  return {
    bytes: response.bytes,
    fileName,
    contentType: attachment.contentType,
    etag,
  };
}

export function announcementOutcomeIsUnknown(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return true;
  return (
    error.code === "GATEWAY_UNREACHABLE" ||
    error.code === "GATEWAY_RESPONSE_INVALID" ||
    error.code === "OPERATOR_IDENTITY_CHANGED" ||
    (error.statusCode !== undefined && error.statusCode >= 500)
  );
}
